import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { smoothScrollToElement } from '../../utils/smoothScroll';

// Floating Neon Orbs Background
const FloatingOrbs = () => {
  const orbs = [
    { size: 300, x: '10%', y: '20%', color: '#FD802E', delay: 0 },
    { size: 200, x: '80%', y: '60%', color: '#00D4FF', delay: 1 },
    { size: 250, x: '60%', y: '10%', color: '#FF006E', delay: 2 },
    { size: 180, x: '30%', y: '70%', color: '#8B5CF6', delay: 0.5 },
  ];

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {orbs.map((orb, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full blur-3xl opacity-10"
          style={{
            width: orb.size,
            height: orb.size,
            left: orb.x,
            top: orb.y,
            background: `radial-gradient(circle, ${orb.color} 0%, transparent 70%)`,
          }}
          animate={{
            x: [0, 30, -20, 0],
            y: [0, -40, 20, 0],
            scale: [1, 1.2, 0.9, 1],
          }}
          transition={{
            duration: 8,
            delay: orb.delay,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      ))}
    </div>
  );
};

// Interactive Particle System
const ParticleField = ({ mousePos }) => {
  const [particles, setParticles] = useState([]);
  
  useEffect(() => {
    const newParticles = Array.from({ length: 50 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 3 + 1,
      speed: Math.random() * 2 + 0.5,
    }));
    setParticles(newParticles);
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map((p) => {
        const dx = mousePos.x - p.x;
        const dy = mousePos.y - p.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const influence = Math.max(0, 1 - dist / 30);
        
        return (
          <motion.div
            key={p.id}
            className="absolute rounded-full"
            style={{
              left: `${p.x}%`,
              top: `${p.y}%`,
              width: p.size,
              height: p.size,
              background: influence > 0.3 ? '#FD802E' : 'rgba(253, 128, 46, 0.3)',
              boxShadow: influence > 0.3 ? '0 0 10px #FD802E, 0 0 20px #FD802E' : 'none',
            }}
            animate={{
              y: [0, -20 * p.speed, 0],
              opacity: [0.3, 0.8, 0.3],
              scale: [1, 1 + influence * 2, 1],
            }}
            transition={{
              duration: 3 / p.speed,
              repeat: Infinity,
              ease: "easeInOut",
              delay: p.id * 0.05,
            }}
          />
        );
      })}
    </div>
  );
};

// Neon Pulse Ring Effect
const NeonPulseRing = ({ isActive, color = '#FD802E' }) => (
  <AnimatePresence>
    {isActive && (
      <>
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            className="absolute inset-0 rounded-full border-2 pointer-events-none"
            style={{ borderColor: color }}
            initial={{ scale: 0.8, opacity: 0.8 }}
            animate={{ scale: 2, opacity: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8, delay: i * 0.15 }}
          />
        ))}
      </>
    )}
  </AnimatePresence>
);

// Glowing Social Media Icon
const NeonSocialIcon = ({ icon, color, href, label }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [showPulse, setShowPulse] = useState(false);
  
  const icons = {
    facebook: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
      </svg>
    ),
    twitter: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
      </svg>
    ),
    youtube: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
      </svg>
    ),
    pinterest: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 0 1 .083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12 0-6.628-5.373-12-12-12z"/>
      </svg>
    ),
    linkedin: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
      </svg>
    ),
  };

  const handleClick = () => {
    setShowPulse(true);
    setTimeout(() => setShowPulse(false), 800);
  };

  return (
    <motion.a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="relative flex items-center gap-3 py-2 group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleClick}
      whileHover={{ x: 8 }}
      transition={{ type: "spring", stiffness: 400 }}
    >
      {/* Icon Container with Neon Glow */}
      <motion.div
        className="relative w-10 h-10 rounded-xl flex items-center justify-center"
        style={{ 
          background: isHovered ? `${color}20` : 'transparent',
          border: `1px solid ${isHovered ? color : 'rgba(255,255,255,0.1)'}`,
        }}
        animate={{
          boxShadow: isHovered 
            ? `0 0 20px ${color}60, 0 0 40px ${color}30, inset 0 0 20px ${color}20` 
            : 'none',
        }}
      >
        <NeonPulseRing isActive={showPulse} color={color} />
        <motion.span
          style={{ color: isHovered ? color : '#9CA3AF' }}
          animate={{ 
            scale: isHovered ? 1.1 : 1,
            filter: isHovered ? `drop-shadow(0 0 8px ${color})` : 'none'
          }}
        >
          {icons[icon]}
        </motion.span>
      </motion.div>

      {/* Label with Neon Underline */}
      <div className="relative">
        <motion.span
          className="text-sm font-medium"
          style={{ color: isHovered ? '#fff' : '#9CA3AF' }}
          animate={{
            textShadow: isHovered ? `0 0 10px ${color}80` : 'none'
          }}
        >
          {label}
        </motion.span>
        
        {/* Animated Neon Underline */}
        <motion.div
          className="absolute -bottom-1 left-0 h-[2px] rounded-full"
          style={{ background: `linear-gradient(90deg, ${color}, transparent)` }}
          initial={{ width: 0 }}
          animate={{ width: isHovered ? '100%' : 0 }}
          transition={{ duration: 0.3 }}
        />
      </div>

      {/* Trailing Glow */}
      <motion.div
        className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 rounded-full"
        style={{ background: color }}
        animate={{
          opacity: isHovered ? [0, 0.5, 0] : 0,
          x: isHovered ? [0, -10, 0] : 0,
        }}
        transition={{ duration: 0.5, repeat: isHovered ? Infinity : 0 }}
      />
    </motion.a>
  );
};

// Neon Footer Link with Glow Effect (supports smooth scrolling to hashes)
const NeonFooterLink = ({ to, children }) => {
  const [isHovered, setIsHovered] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleClick = (e) => {
    // Handle hash-only navigation
    const hash = to.includes('#') ? to.split('#').pop() : null;
    
    if (hash) {
      e.preventDefault();
      
      if (location.pathname === '/') {
        // Already on home page, scroll to section
        smoothScrollToElement(hash, 100);
      } else {
        // Navigate to home page with scroll state
        navigate('/', { state: { scrollToSection: hash } });
      }
    } else if (to.startsWith('/')) {
      // Full path navigation
      navigate(to);
    }
  };

  const content = (
    <motion.div
      className="relative py-1.5 cursor-pointer"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      whileHover={{ x: 5 }}
    >
      {/* Glow Bar */}
      <motion.div
        className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-4 bg-neon-orange rounded-full"
        initial={{ opacity: 0, x: -10 }}
        animate={{ 
          opacity: isHovered ? 1 : 0, 
          x: isHovered ? -8 : -10,
          boxShadow: isHovered ? '0 0 10px #FD802E, 0 0 20px #FD802E' : 'none'
        }}
      />
      
      <motion.span
        className="text-sm"
        animate={{
          color: isHovered ? '#FD802E' : '#9CA3AF',
          textShadow: isHovered ? '0 0 10px rgba(253, 128, 46, 0.5)' : 'none'
        }}
      >
        {children}
      </motion.span>
    </motion.div>
  );

  // If target is full path (no hash) keep Link behavior, otherwise use anchor with custom scroll
  if (!to || to === '/' || !to.includes('#')) {
    return <Link to={to || '/'}>{content}</Link>;
  }

  // For hash links (e.g. '#features' or '/#features') use anchor with custom handler
  return (
    <a href={to} onClick={handleClick}>
      {content}
    </a>
  );
};

// Glowing Newsletter Form
const NeonNewsletterForm = () => {
  const [email, setEmail] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [sparkles, setSparkles] = useState([]);
  const validateEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !validateEmail(email)) return;

    setIsSubmitting(true);

    // Create sparkle explosion
    const newSparkles = Array.from({ length: 20 }, (_, i) => ({
      id: Date.now() + i,
      angle: (i / 20) * 360,
      distance: 50 + Math.random() * 50,
    }));
    setSparkles(newSparkles);

    try {
      const res = await fetch('https://formspree.io/f/mdawynrq', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ email, source: 'newsletter' }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        console.error('Formspree newsletter error', data);
        throw new Error(data.error || 'Failed to subscribe');
      }

      setIsSubmitted(true);
      setEmail('');
      setSparkles([]);
      setTimeout(() => setIsSubmitted(false), 3000);
    } catch (err) {
      console.error(err);
      alert('Unable to subscribe. Please try again later.');
      setSparkles([]);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="mt-4 relative">
      {/* Sparkle Effects */}
      <AnimatePresence>
        {sparkles.map((s) => (
          <motion.div
            key={s.id}
            className="absolute right-12 top-1/2 w-2 h-2 bg-neon-orange rounded-full pointer-events-none"
            initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
            animate={{
              x: Math.cos((s.angle * Math.PI) / 180) * s.distance,
              y: Math.sin((s.angle * Math.PI) / 180) * s.distance,
              opacity: 0,
              scale: 0,
            }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            style={{ boxShadow: '0 0 10px #FD802E, 0 0 20px #FD802E' }}
          />
        ))}
      </AnimatePresence>

      <motion.div 
        className="relative flex rounded-full overflow-hidden"
        animate={{
          boxShadow: isFocused 
            ? '0 0 20px rgba(253, 128, 46, 0.3), 0 0 40px rgba(253, 128, 46, 0.1), inset 0 0 20px rgba(253, 128, 46, 0.05)'
            : '0 0 0 rgba(253, 128, 46, 0)'
        }}
      >
        {/* Animated Border */}
        <motion.div
          className="absolute inset-0 rounded-full pointer-events-none"
          style={{
            background: 'linear-gradient(90deg, #FD802E, #FF006E, #00D4FF, #FD802E)',
            backgroundSize: '300% 100%',
          }}
          animate={{
            backgroundPosition: isFocused ? ['0% 0%', '100% 0%'] : '0% 0%',
            opacity: isFocused ? 1 : 0,
          }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
        />
        
        <div className="relative flex w-full bg-dark-deeper m-[2px] rounded-full flex-col sm:flex-row items-stretch p-1">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            placeholder="Email address"
            className="flex-1 min-w-0 bg-transparent px-5 py-3 text-sm text-white placeholder-gray-500 focus:outline-none rounded-full sm:rounded-l-full sm:rounded-r-none"
          />
          <motion.button
            type="submit"
            disabled={isSubmitting || isSubmitted}
            className="relative bg-neon-orange text-dark-base font-bold px-6 py-3 text-sm disabled:opacity-50 overflow-hidden w-full sm:w-auto mt-3 sm:mt-0 rounded-full sm:rounded-r-full sm:rounded-l-none flex-shrink-0"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {/* Button Shine Effect */}
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
              initial={{ x: '-100%' }}
              whileHover={{ x: '100%' }}
              transition={{ duration: 0.5 }}
            />
            <span className="relative z-10">
              {isSubmitting ? (
                <motion.span
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                >
                  ◌
                </motion.span>
              ) : isSubmitted ? '✓' : 'JOIN'}
            </span>
          </motion.button>
        </div>
      </motion.div>
    </form>
  );
};

// Animated Neon Section Header
const NeonSectionHeader = ({ children, delay = 0 }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <motion.h3
      className="relative text-sm font-heading font-bold tracking-wider mb-5 cursor-default inline-block"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <motion.span
        animate={{
          color: isHovered ? '#FD802E' : '#ffffff',
          textShadow: isHovered ? '0 0 20px #FD802E, 0 0 40px #FD802E' : 'none'
        }}
      >
        {children}
      </motion.span>
      
      {/* Animated Underline */}
      <motion.div
        className="absolute -bottom-2 left-0 h-[2px] bg-gradient-to-r from-neon-orange to-transparent"
        initial={{ width: 0 }}
        whileInView={{ width: '70%' }}
        viewport={{ once: true }}
        transition={{ delay: delay + 0.3, duration: 0.5 }}
      />
    </motion.h3>
  );
};

// Interactive Email Link with Neon Effect
const NeonEmailLink = () => {
  const [isHovered, setIsHovered] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const linkRef = useRef(null);

  const handleMouseMove = (e) => {
    if (linkRef.current) {
      const rect = linkRef.current.getBoundingClientRect();
      setMousePos({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    }
  };

  return (
    <motion.a
      ref={linkRef}
      href="mailto:team@veritonetech.com"
      className="relative text-2xl md:text-3xl lg:text-4xl font-bold overflow-hidden inline-block"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onMouseMove={handleMouseMove}
      initial={{ opacity: 0, x: 20 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
    >
      {/* Cursor Follow Glow */}
      <motion.div
        className="absolute w-32 h-32 rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(253, 128, 46, 0.4) 0%, transparent 70%)',
          left: mousePos.x - 64,
          top: mousePos.y - 64,
        }}
        animate={{ opacity: isHovered ? 1 : 0 }}
      />
      
      <motion.span
        className="relative z-10"
        animate={{
          color: isHovered ? '#ffffff' : '#FD802E',
          textShadow: isHovered 
            ? '0 0 30px #FD802E, 0 0 60px #FD802E, 0 0 90px #FD802E' 
            : '0 0 10px rgba(253, 128, 46, 0.3)'
        }}
      >
        team@veritonetech.com
      </motion.span>
      
      {/* Animated Underline */}
      <motion.div
        className="absolute bottom-0 left-0 h-[3px] bg-neon-orange rounded-full"
        style={{ boxShadow: '0 0 10px #FD802E, 0 0 20px #FD802E' }}
        initial={{ width: '100%' }}
        animate={{ 
          scaleX: isHovered ? 1.05 : 1,
          opacity: isHovered ? [1, 0.5, 1] : 1
        }}
        transition={{ duration: 0.5, repeat: isHovered ? Infinity : 0 }}
      />
    </motion.a>
  );
};

// Animated Watermark with Neon Outline
const NeonWatermark = () => {
  const [mouseX, setMouseX] = useState(0);
  const containerRef = useRef(null);

  const handleMouseMove = useCallback((e) => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setMouseX((e.clientX - rect.left) / rect.width);
    }
  }, []);

  return (
    <div 
      ref={containerRef}
      className="relative overflow-hidden pointer-events-auto select-none cursor-default"
      onMouseMove={handleMouseMove}
    >
      {/* Gradient Mask based on mouse */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(circle at ${mouseX * 100}% 50%, rgba(253, 128, 46, 0.15) 0%, transparent 50%)`,
        }}
      />
      
      <motion.div 
        className="text-[6rem] md:text-[10rem] lg:text-[14rem] font-black whitespace-nowrap text-center leading-none py-8"
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        style={{ 
          color: 'transparent',
          WebkitTextStroke: '2px rgba(253, 128, 46, 0.2)',
          textShadow: '0 0 40px rgba(253, 128, 46, 0.1)',
        }}
      >
        <motion.span
          animate={{
            WebkitTextStroke: [
              '2px rgba(253, 128, 46, 0.2)',
              '2px rgba(253, 128, 46, 0.4)',
              '2px rgba(253, 128, 46, 0.2)',
            ],
            textShadow: [
              '0 0 40px rgba(253, 128, 46, 0.1)',
              '0 0 60px rgba(253, 128, 46, 0.2)',
              '0 0 40px rgba(253, 128, 46, 0.1)',
            ],
          }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
        >
          VERITONETECH
        </motion.span>
      </motion.div>
      
      {/* Scanning Line Effect */}
      <motion.div
        className="absolute top-0 bottom-0 w-[2px] bg-gradient-to-b from-transparent via-neon-orange/50 to-transparent pointer-events-none"
        animate={{ left: ['-5%', '105%'] }}
        transition={{ duration: 4, repeat: Infinity, ease: "linear", repeatDelay: 2 }}
      />
    </div>
  );
};

// Audio Visualizer Bar
const AudioVisualizer = () => {
  const [bars, setBars] = useState(Array(60).fill(5));
  
  useEffect(() => {
    const interval = setInterval(() => {
      setBars(prev => prev.map(() => Math.random() * 20 + 3));
    }, 100);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="absolute bottom-0 left-0 right-0 h-12 flex items-end justify-center gap-[2px] opacity-30">
      {bars.map((height, i) => (
        <motion.div
          key={i}
          className="w-1 bg-gradient-to-t from-neon-orange/50 to-neon-orange rounded-t-full"
          animate={{ height }}
          transition={{ duration: 0.1 }}
          style={{
            boxShadow: height > 15 ? '0 0 5px #FD802E' : 'none'
          }}
        />
      ))}
    </div>
  );
};

const Footer = () => {
  const [mousePos, setMousePos] = useState({ x: 50, y: 50 });
  const footerRef = useRef(null);

  const handleMouseMove = (e) => {
    if (footerRef.current) {
      const rect = footerRef.current.getBoundingClientRect();
      setMousePos({
        x: ((e.clientX - rect.left) / rect.width) * 100,
        y: ((e.clientY - rect.top) / rect.height) * 100,
      });
    }
  };

  const pages = [
    { label: 'Home', to: '/' },
    { label: 'Features', to: '#features' },
    { label: 'About', to: '#about' },
    { label: 'Contact', to: '#contact' },
    { label: 'Privacy Policy', to: '/privacy' },
    { label: 'Terms of Service', to: '/terms' },
  ];

  const quickLinks = [
    { label: 'How It Works', to: '#how-it-works' },
    { label: 'Pricing', to: '#pricing' },
    { label: 'Customer Reviews', to: '#testimonials' },
    { label: 'AI Engine', to: '/veritonetech-ai' },
  ];

  const socialLinks = [
    { icon: 'youtube', color: '#FF0000', href: 'https://www.youtube.com/@VeritoneTech', label: 'YouTube' },
    { icon: 'pinterest', color: '#E60023', href: 'https://www.pinterest.com/veritonetech/', label: 'Pinterest' },
    { icon: 'facebook', color: '#1877F2', href: 'https://www.facebook.com/Veritonetech/', label: 'Facebook' },
    { icon: 'twitter', color: '#ffffff', href: 'https://x.com/Veritone_Tech', label: 'X' },
    { icon: 'linkedin', color: '#0A66C2', href: 'https://www.linkedin.com/company/veritone-tech/', label: 'LinkedIn' },
  ];

  return (
    <footer 
      ref={footerRef}
      className="relative bg-dark-deeper overflow-hidden"
      onMouseMove={handleMouseMove}
    >
      {/* Background Effects */}
      <FloatingOrbs />
      <ParticleField mousePos={mousePos} />
      
      

      {/* Main Footer Content */}
      <div className="relative max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 md:gap-16">
          {/* Pages Column */}
          <div>
            <NeonSectionHeader delay={0.1}>PAGES</NeonSectionHeader>
            <nav className="flex flex-col gap-1">
              {pages.map((link, i) => (
                <motion.div
                  key={link.label}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 + i * 0.05 }}
                >
                  <NeonFooterLink to={link.to}>{link.label}</NeonFooterLink>
                </motion.div>
              ))}
            </nav>
          </div>

          {/* Quick Links Column */}
          <div>
            <NeonSectionHeader delay={0.2}>QUICK LINKS</NeonSectionHeader>
            <nav className="flex flex-col gap-1">
              {quickLinks.map((link, i) => (
                <motion.div
                  key={link.label}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 + i * 0.05 }}
                >
                  <NeonFooterLink to={link.to}>{link.label}</NeonFooterLink>
                </motion.div>
              ))}
            </nav>
          </div>

          {/* Social Media Column */}
          <div>
            <NeonSectionHeader delay={0.3}>SOCIAL MEDIA</NeonSectionHeader>
            <nav className="flex flex-col gap-1">
              {socialLinks.map((link, i) => (
                <motion.div
                  key={link.icon}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4 + i * 0.05 }}
                >
                  <NeonSocialIcon {...link} />
                </motion.div>
              ))}
            </nav>
          </div>

          {/* Newsletter Column */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="col-span-2 md:col-span-1"
          >
            <NeonSectionHeader delay={0.4}>NEWSLETTER</NeonSectionHeader>
            <p className="text-gray-400 text-sm leading-relaxed mb-2">
              Get the latest updates on features, releases, and insights — straight to your inbox.
            </p>
            <NeonNewsletterForm />
          </motion.div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="relative border-t border-neon-orange/10">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-4">
              <img src="/logo.svg" alt="SoundFlowAI logo" className="w-48 h-auto" />
              <motion.p 
                className="text-gray-500 text-sm"
                whileHover={{ color: '#9CA3AF' }}
              >
                © 2026 VeritoneTech. All rights reserved.
              </motion.p>
            </div>
            <motion.p 
              className="text-gray-500 text-sm"
              whileHover={{ color: '#FD802E', textShadow: '0 0 10px rgba(253, 128, 46, 0.5)' }}
            >
              Built for creators who innovate fast.
            </motion.p>
          </div>
        </div>
      </div>

      {/* Large Watermark Text */}
      <NeonWatermark />
      
      {/* Audio Visualizer */}
      <AudioVisualizer />
    </footer>
  );
};

export default Footer;
